/**
 * FRE-139: the one catalogue of guitar (6-10 strings) and bass (4-8 strings, plus multi-course 8/10/12) tunings. Everything that shows, stores, filters or tunes to a
 * tuning goes through this file. Tab `tuning` values in the DB are free-form strings; new ones are the canonical
 * `name` below, and legacy values ("E Std", "Standard E", "B Standard" = 7-string) resolve through `findTuning`.
 *
 * Notes are stored low -> high (thickest string first) with sharps in ASCII ("A#1"). Names are Latin and must be
 * rendered LTR (music notation). `npm run tunings:export` copies the list into the API repo for the bulk-upload script.
 */
import type { TuningPreset } from '../types/index.js';

export type TuningCategory = 'standard' | 'drop' | 'open' | 'modal' | 'multicourse' | 'custom';
export type Instrument = 'guitar' | 'bass';
export type StringCount = 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface Tuning {
  id: string;
  /** Guitar (default) or bass. Bass ids start with `bass-`. */
  instrument: Instrument;
  /** Canonical English name, the value stored on tabs/requests. Unique across the catalogue. */
  name: string;
  strings: StringCount;
  /** Low to high, e.g. ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'] */
  notes: string[];
  category: TuningCategory;
  /** Who uses it / what it is for (English, shown under the picker). */
  context?: string;
}

/** Type key of the multi-course group in the picker ("چندکورس"): paired/tripled bass courses with 8, 10 or 12 strings. */
export const MULTICOURSE_KEY = 'multi';
export interface InstrumentType {
  /** String count as a string, or MULTICOURSE_KEY. */
  key: string;
  label: string;
  shortLabel: string;
}
const countTypes = (counts: number[], noun: string): InstrumentType[] =>
  counts.map((n) => ({ key: String(n), label: `${noun} ${['', '', '', '', '۴', '۵', '۶', '۷', '۸', '۹', '۱۰'][n]} سیم`, shortLabel: `${['', '', '', '', '۴', '۵', '۶', '۷', '۸', '۹', '۱۰'][n]} سیم` }));

export const INSTRUMENTS: Record<Instrument, { label: string; types: InstrumentType[] }> = {
  guitar: { label: 'گیتار', types: countTypes([6, 7, 8, 9, 10], 'گیتار') },
  bass: {
    label: 'بیس',
    types: [...countTypes([4, 5, 6, 7, 8], 'بیس'), { key: MULTICOURSE_KEY, label: 'بیس چندکورس', shortLabel: 'چندکورس' }]
  }
};
/** Kept for older call sites: the guitar string counts. */
export const GUITAR_TYPES: Array<{ strings: StringCount; label: string }> = INSTRUMENTS.guitar.types.map((t) => ({
  strings: Number(t.key) as StringCount,
  label: t.shortLabel
}));

export const CATEGORY_LABELS: Record<TuningCategory, string> = {
  standard: 'استاندارد و پایین‌تر',
  drop: 'دراپ',
  open: 'اوپن',
  modal: 'مودال و جایگزین',
  multicourse: 'چندکورس (جفت و سه‌تایی)',
  custom: 'کوک‌های من'
};
export const CATEGORY_ORDER: TuningCategory[] = ['standard', 'drop', 'open', 'modal', 'multicourse', 'custom'];

// [id, name, strings, notes (low -> high), category, context]
type Row = [string, string, StringCount, string, TuningCategory, string?];
const ROWS: Row[] = [
  // 6-string: standard and step-down
  ['standard-e', 'E Standard', 6, 'E2 A2 D3 G3 B3 E4', 'standard', 'Universal standard'],
  ['eb-standard', 'Eb Standard', 6, 'D#2 G#2 C#3 F#3 A#3 D#4', 'standard', "Hendrix, SRV, Guns N' Roses, Alice in Chains"],
  ['d-standard', 'D Standard', 6, 'D2 G2 C3 F3 A3 D4', 'standard', 'Motley Crue, Death, Ghost'],
  ['c-sharp-standard', 'C# Standard', 6, 'C#2 F#2 B2 E3 G#3 C#4', 'standard', 'Black Sabbath, Behemoth'],
  ['c-standard', 'C Standard', 6, 'C2 F2 A#2 D#3 G3 C4', 'standard', 'Queens of the Stone Age, In Flames, Dethklok'],
  ['b-baritone', 'B Standard (Baritone)', 6, 'B1 E2 A2 D3 F#3 B3', 'standard', 'Amon Amarth, Carcass, baritone 6-strings'],
  ['a-sharp-standard', 'A# Standard', 6, 'A#1 D#2 G#2 C#3 F3 A#3', 'standard', 'Cannibal Corpse, extreme doom'],
  ['a-standard', 'A Standard', 6, 'A1 D2 G2 C3 E3 A3', 'standard', 'Arch Enemy, doom metal'],
  // 6-string: drop
  ['drop-d', 'Drop D', 6, 'D2 A2 D3 G3 B3 E4', 'drop', 'Tool, Soundgarden, Foo Fighters, Lamb of God'],
  ['drop-c-sharp', 'Drop C#', 6, 'C#2 G#2 C#3 F#3 A#3 D#4', 'drop', 'Deftones, Avenged Sevenfold, Alter Bridge'],
  ['drop-c', 'Drop C', 6, 'C2 G2 C3 F3 A3 D4', 'drop', 'Killswitch Engage, System of a Down, Bullet For My Valentine'],
  ['drop-b', 'Drop B', 6, 'B1 F#2 B2 E3 G#3 C#4', 'drop', 'Slipknot, Bring Me The Horizon (early), Parkway Drive'],
  ['drop-a-sharp', 'Drop A#', 6, 'A#1 F2 A#2 D#3 G3 C4', 'drop', 'Breaking Benjamin, Parkway Drive (later)'],
  ['drop-a', 'Drop A', 6, 'A1 E2 A2 D3 F#3 B3', 'drop', 'Slipknot (Iowa), Red Fang'],
  ['drop-g-sharp', 'Drop G#', 6, 'G#1 D#2 G#2 C#3 F3 A#3', 'drop', 'Modern downtuned metalcore / deathcore on baritone 6s'],
  ['double-drop-d', 'Double Drop D', 6, 'D2 A2 D3 G3 B3 D4', 'drop', 'Neil Young, Led Zeppelin'],
  // 6-string: open
  ['open-d', 'Open D', 6, 'D2 A2 D3 F#3 A3 D4', 'open', 'D major: slide guitar, blues, folk'],
  ['open-e', 'Open E', 6, 'E2 B2 E3 G#3 B3 E4', 'open', 'E major: Derek Trucks, Duane Allman'],
  ['open-g', 'Open G', 6, 'D2 G2 D3 G3 B3 D4', 'open', 'G major: Rolling Stones (Keith Richards), delta blues'],
  ['open-a', 'Open A', 6, 'E2 A2 E3 A3 C#4 E4', 'open', 'A major: country blues, slide'],
  ['open-c', 'Open C', 6, 'C2 G2 C3 G3 C4 E4', 'open', 'C major: Devin Townsend, John Fahey'],
  ['open-d-minor', 'Open D Minor', 6, 'D2 A2 D3 F3 A3 D4', 'open', 'D minor: fingerstyle, Scandinavian folk'],
  ['open-g-minor', 'Open G Minor', 6, 'D2 G2 D3 G3 A#3 D4', 'open', 'G minor: delta blues slide'],
  // 6-string: modal and alternate
  ['dadgad', 'DADGAD', 6, 'D2 A2 D3 G3 A3 D4', 'modal', 'Celtic folk, Jimmy Page (Kashmir)'],
  ['daddad', 'DADDAD', 6, 'D2 A2 D3 D3 A3 D4', 'modal', 'Extreme drone, full D5 power chord resonance'],
  ['nashville', 'Nashville', 6, 'E3 A3 D4 G4 B3 E4', 'modal', 'High-octave strings from a 12-string set'],
  ['all-fourths', 'All Fourths', 6, 'E2 A2 D3 G3 C4 F4', 'modal', 'Symmetric fretboard geometry across all strings'],
  ['all-fifths', 'All Fifths (New Standard)', 6, 'C2 G2 D3 A3 E4 G4', 'modal', 'Robert Fripp (King Crimson), violin-style tuning'],
  ['orkney', 'Orkney', 6, 'C2 G2 D3 G3 C4 D4', 'modal', 'Traditional acoustic / Celtic fingerstyle'],
  // 7-string
  ['b-standard', '7-String B Standard', 7, 'B1 E2 A2 D3 G3 B3 E4', 'standard', 'Standard 7-string baseline (Dream Theater, Korn, Trivium)'],
  ['7-a-sharp-standard', '7-String A# Standard', 7, 'A#1 D#2 G#2 C#3 F#3 A#3 D#4', 'standard', 'Half-step down (Nevermore, Meshuggah Destroy Erase Improve)'],
  ['7-a-standard', '7-String A Standard', 7, 'A1 D2 G2 C3 F3 A3 D4', 'standard', 'Full step down (Korn, Morbid Angel)'],
  ['7-drop-a', '7-String Drop A', 7, 'A1 E2 A2 D3 G3 B3 E4', 'drop', 'Most common 7-string drop (Slipknot, Whitechapel, Chelsea Grin)'],
  ['7-drop-g-sharp', '7-String Drop G#', 7, 'G#1 D#2 G#2 C#3 F#3 A#3 D#4', 'drop', 'Half-step down drop (Spiritbox, Periphery, Erra)'],
  ['7-drop-g', '7-String Drop G', 7, 'G1 D2 G2 C3 F3 A3 D4', 'drop', 'Full step down drop (Born of Osiris, Fit For An Autopsy)'],
  ['7-drop-f-sharp', '7-String Drop F#', 7, 'F#1 C#2 F#2 B2 E3 G#3 C#4', 'drop', 'Low registers (Architects, Volumes)'],
  ['7-drop-f', '7-String Drop F', 7, 'F1 C2 F2 A#2 D#3 G3 C4', 'drop', 'Extreme downtuned metalcore (Alpha Wolf)'],
  ['7-open-d', '7-String Open D', 7, 'D1 D2 A2 D3 F#3 A3 D4', 'open', 'Acoustic extended range slide / fingerstyle'],
  ['7-double-drop-a', '7-String Double Drop A', 7, 'A1 D2 A2 D3 G3 B3 E4', 'drop', 'Drop D on top with an added low A a fifth below'],
  // 8-string
  ['8-f-sharp-standard', '8-String F# Standard', 8, 'F#1 B1 E2 A2 D3 G3 B3 E4', 'standard', 'Factory standard 8-string (Meshuggah, Animals as Leaders)'],
  ['8-f-standard', '8-String F Standard', 8, 'F1 A#1 D#2 G#2 C#3 F#3 A#3 D#4', 'standard', 'Meshuggah standard tuning (obZen, Koloss)'],
  ['8-drop-e', '8-String Drop E', 8, 'E1 B1 E2 A2 D3 G3 B3 E4', 'drop', 'Matches low E on a standard 4-string bass'],
  ['8-drop-eb', '8-String Drop Eb', 8, 'D#1 A#1 D#2 G#2 C#3 F#3 A#3 D#4', 'drop', 'Half-step down drop'],
  ['8-drop-d', '8-String Drop D', 8, 'D1 A1 D2 G2 C3 F3 A3 D4', 'drop', 'Full octave below standard Drop D (Haken, modern djent)'],
  ['8-drop-c-sharp', '8-String Drop C#', 8, 'C#1 G#1 C#2 F#2 B2 E3 G#3 C#4', 'drop', 'Extreme extended range'],
  ['8-aal-hybrid', '8-String AAL Hybrid', 8, 'E1 A1 E2 A2 D3 G3 B3 E4', 'modal', 'Tosin Abasi dual-drop tuning (stacked fifths and fourths)'],
  ['8-high-a', '8-String High A', 8, 'B1 E2 A2 D3 G3 B3 E4 A4', 'modal', 'Adds a high A instead of a low F# (needs ultralight strings)'],
  // 9-string
  ['9-c-sharp-standard', '9-String C# Standard', 9, 'C#1 F#1 B1 E2 A2 D3 G3 B3 E4', 'standard', 'Factory 9-string standard (Ibanez RG9/TAM9)'],
  ['9-b0-standard', '9-String B0 Standard', 9, 'B0 E1 A1 D2 G2 B2 E3 A3 D4', 'standard', 'Lower register in fourths'],
  ['9-drop-b', '9-String Drop B', 9, 'B0 F#1 B1 E2 A2 D3 G3 B3 E4', 'drop', '9th string at B0 (about 30.9 Hz, below a 4-string bass low E)'],
  ['9-drop-a', '9-String Drop A', 9, 'A0 E1 A1 D2 G2 C3 F3 A3 D4', 'drop', 'A0 is about 27.5 Hz, the lowest key of an 88-key piano'],
  // 10-string
  ['10-standard', '10-String Standard', 10, 'G#0 C#1 F#1 B1 E2 A2 D3 G3 B3 E4', 'standard', 'Modern 10-string extended range'],
  ['10-drop-f-sharp', '10-String Drop F#', 10, 'F#0 C#1 F#1 B1 E2 A2 D3 G3 B3 E4', 'drop', '10th string at F#0 (about 23 Hz)'],
  ['10-classical-yepes', '10-String Classical (Yepes)', 10, 'F#1 G#1 A#1 C2 E2 A2 D3 G3 B3 E4', 'modal', 'Narciso Yepes: sympathetic resonance for lute transcriptions']
];


// Bass guitar: same row layout. Ids are prefixed `bass-`. Multi-course rows list every physical string, course by course
// (so notes may repeat or step down; that is intended).
const BASS_ROWS: Row[] = [
  // 4-string: standard and step-down
  ['bass-e-standard', 'Bass E Standard', 4, 'E1 A1 D2 G2', 'standard', 'Universal bass baseline (E1 is about 41.2 Hz)'],
  ['bass-eb-standard', 'Bass Eb Standard', 4, 'D#1 G#1 C#2 F#2', 'standard', "Guns N' Roses, Alice in Chains, Jimi Hendrix"],
  ['bass-d-standard', 'Bass D Standard', 4, 'D1 G1 C2 F2', 'standard', 'Motley Crue, Death, Pantera select tracks, Ghost'],
  ['bass-c-sharp-standard', 'Bass C# Standard', 4, 'C#1 F#1 B1 E2', 'standard', 'Black Sabbath, Blink-182 later, Behemoth'],
  ['bass-c-standard', 'Bass C Standard', 4, 'C1 F1 A#1 D#2', 'standard', 'Queens of the Stone Age, Dethklok, The Black Dahlia Murder'],
  ['bass-bead', 'Bass B-E-A-D (Low B)', 4, 'B0 E1 A1 D2', 'standard', 'Bottom 4 strings of a 5-string set'],
  ['bass-adgc', 'Bass A-D-G-C', 4, 'A0 D1 G1 C2', 'standard', 'Sub-octave step-down for doom / sludge'],
  // 4-string: drop
  ['bass-drop-d', 'Bass Drop D', 4, 'D1 A1 D2 G2', 'drop', 'Tool, Rage Against the Machine, Soundgarden'],
  ['bass-drop-c-sharp', 'Bass Drop C#', 4, 'C#1 G#1 C#2 F#2', 'drop', 'Deftones, Avenged Sevenfold, Chevelle'],
  ['bass-drop-c', 'Bass Drop C', 4, 'C1 G1 C2 F2', 'drop', 'Killswitch Engage, System of a Down, Mastodon'],
  ['bass-drop-b', 'Bass Drop B', 4, 'B0 F#1 B1 E2', 'drop', 'Slipknot, Bring Me The Horizon, Architects (early)'],
  ['bass-drop-a-sharp', 'Bass Drop A#', 4, 'A#0 F1 A#1 D#2', 'drop', 'Breaking Benjamin, Parkway Drive'],
  ['bass-drop-a', 'Bass Drop A', 4, 'A0 E1 A1 D2', 'drop', 'Slipknot (Iowa), Muse, Red Fang'],
  // 4-string: modal
  ['bass-tenor', 'Tenor Bass', 4, 'A1 D2 G2 C3', 'modal', 'Chordal work, melodic leads, solo bass'],
  ['bass-piccolo', 'Piccolo Bass', 4, 'E2 A2 D3 G3', 'modal', 'One octave above standard bass'],
  ['bass-all-fifths', 'Bass All Fifths (Cello)', 4, 'C1 G1 D2 A2', 'modal', 'Classical crossover / jazz'],
  ['bass-dadg', 'Bass DADG (Open D)', 4, 'D1 A1 D2 F#2', 'modal', 'Slide bass, drone styles'],
  // 5-string
  ['bass-5-b-standard', '5-String Bass B Standard', 5, 'B0 E1 A1 D2 G2', 'standard', 'Factory standard 5-string: rock, gospel, jazz, pop'],
  ['bass-5-a-sharp-standard', '5-String Bass A# Standard', 5, 'A#0 D#1 G#1 C#2 F#2', 'standard', 'Half-step down'],
  ['bass-5-a-standard', '5-String Bass A Standard', 5, 'A0 D1 G1 C2 F2', 'standard', 'Korn, Fieldy'],
  ['bass-5-drop-a', '5-String Bass Drop A', 5, 'A0 E1 A1 D2 G2', 'drop', 'Slipknot, Whitechapel, Gojira'],
  ['bass-5-drop-g-sharp', '5-String Bass Drop G#', 5, 'G#0 D#1 G#1 C#2 F#2', 'drop', 'Spiritbox, Periphery, Currents'],
  ['bass-5-drop-g', '5-String Bass Drop G', 5, 'G0 D1 G1 C2 F2', 'drop', 'G0 is about 24.5 Hz; Fit For An Autopsy, Born of Osiris'],
  ['bass-5-drop-f-sharp', '5-String Bass Drop F#', 5, 'F#0 C#1 F#1 B1 E2', 'drop', 'About 23.12 Hz; Architects, ERRA'],
  ['bass-5-high-c', '5-String Bass High C (Tenor 5)', 5, 'E1 A1 D2 G2 C3', 'modal', 'Fusion / chordal: Janek Gwizdala, Matt Garrison, Victor Wooten'],
  // 6-string
  ['bass-6-standard', '6-String Bass Standard', 6, 'B0 E1 A1 D2 G2 C3', 'standard', 'John Patitucci, Thundercat, Dream Theater'],
  ['bass-6-a-standard', '6-String Bass A Standard', 6, 'A0 D1 G1 C2 F2 A#2', 'standard', 'Full step down'],
  ['bass-6-drop-a', '6-String Bass Drop A', 6, 'A0 E1 A1 D2 G2 C3', 'drop', 'Drop A on a 6-string set'],
  ['bass-6-drop-g', '6-String Bass Drop G', 6, 'G0 D1 G1 C2 F2 A#2', 'drop', 'Progressive deathcore, djent'],
  ['bass-vi', 'Bass VI (Fender)', 6, 'E1 A1 D2 G2 B2 E3', 'modal', 'Guitar tuning one octave down, keeps the major third G-B'],
  // 7/8-string
  ['bass-7-f-sharp-standard', '7-String Bass F# Standard', 7, 'F#0 B0 E1 A1 D2 G2 C3', 'standard', 'F#0 is about 23.12 Hz; needs a 35-37 inch multiscale'],
  ['bass-7-high-f', '7-String Bass High F', 7, 'B0 E1 A1 D2 G2 C3 F3', 'modal', 'Classical melody, solo bass'],
  ['bass-8-f-sharp-standard', '8-String Bass F# Standard', 8, 'F#0 B0 E1 A1 D2 G2 C3 F3', 'standard', 'Extended range solo bass'],
  // multi-course (every physical string, low to high inside each course, courses in order of pitch)
  ['bass-4-course-octaves', 'Bass 4-Course Octaves (8-String)', 8, 'E1 E2 A1 A2 D2 D3 G2 G3', 'multicourse', 'Pearl Jam, Cheap Trick'],
  ['bass-5-course-octaves', 'Bass 5-Course Octaves (10-String)', 10, 'B0 B1 E1 E2 A1 A2 D2 D3 G2 G3', 'multicourse', 'Gothic rock, industrial, doom'],
  ['bass-4-course-tripled', 'Bass 4-Course Tripled (12-String)', 12, 'E1 E2 E2 A1 A2 A2 D2 D3 D3 G2 G3 G3', 'multicourse', "King's X, Cheap Trick"]
];

const NOTE_INDEX: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** 'A#1' -> MIDI number (C4 = 60, E2 = 40) */
export function noteToMidi(note: string): number {
  const m = /^([A-G])(#?)(-?\d+)$/.exec(note);
  if (!m) throw new Error(`Bad note "${note}"`);
  return (Number(m[3]) + 1) * 12 + NOTE_INDEX[m[1]] + (m[2] ? 1 : 0);
}

export function midiToNote(midi: number): { note: string; octave: number } {
  return { note: SHARP_NAMES[((midi % 12) + 12) % 12], octave: Math.floor(midi / 12) - 1 };
}

/** Equal-tempered frequency of a note (A4 = 440 unless told otherwise). */
export function noteFrequency(note: string, a4 = 440): number {
  return a4 * Math.pow(2, (noteToMidi(note) - 69) / 12);
}

const toTuning = (instrument: Instrument) => ([id, name, strings, notes, category, context]: Row): Tuning => ({
  id,
  instrument,
  name,
  strings,
  notes: notes.split(' '),
  category,
  context
});
export const TUNINGS: Tuning[] = [...ROWS.map(toTuning('guitar')), ...BASS_ROWS.map(toTuning('bass'))];

/** Picker type key of a tuning: its string count, or MULTICOURSE_KEY for multi-course / 11-12 note bass values. */
export const typeKeyOf = (t: Tuning): string => (t.category === 'multicourse' || (t.instrument === 'bass' && t.strings > 8) ? MULTICOURSE_KEY : String(t.strings));

/** Catalogue tunings of one instrument and string count (or the MULTICOURSE_KEY group). */
export const tuningsForType = (instrument: Instrument, strings: number | string): Tuning[] =>
  TUNINGS.filter((t) => t.instrument === instrument && typeKeyOf(t) === String(strings));

export function groupByCategory(list: Tuning[]): Array<{ category: TuningCategory; label: string; tunings: Tuning[] }> {
  return CATEGORY_ORDER.map((category) => ({ category, label: CATEGORY_LABELS[category], tunings: list.filter((t) => t.category === category) })).filter(
    (g) => g.tunings.length > 0
  );
}

// ---- custom tunings (stored as the free-form tab `tuning` string) -----------------------------------------------------

export const CUSTOM_DEFAULT_NAME = 'Custom';
export const CUSTOM_NAME_MAX = 40;
const NOTE_RE = /^([A-G])(#?)(\d)$/;
const CUSTOM_RE = /^([^()]*)\(([^()]*)\)$/;

const isValidNote = (n: string): boolean => {
  const m = NOTE_RE.exec(n);
  return Boolean(m) && Number(m![3]) >= 0 && Number(m![3]) <= 8;
};

const cleanCustomName = (name: string | undefined): string =>
  (name ?? '').replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, CUSTOM_NAME_MAX).trim() || CUSTOM_DEFAULT_NAME;

/** `<name> (<notes low to high>)`, e.g. "Custom (D2 A2 D3 G3 B3 E4)". Name is optional (default "Custom"), max 40 chars, no parentheses. */
export function formatCustomTuning(name: string | undefined, notes: string[]): string {
  return `${cleanCustomName(name)} (${notes.join(' ')})`;
}

/** Reads a stored custom value back. Undefined unless it is `<name> (<4-12 valid notes, octave 0-8>)`. */
export function parseCustomTuning(raw: string | undefined | null): { name: string; notes: string[]; strings: StringCount } | undefined {
  if (!raw) return undefined;
  const m = CUSTOM_RE.exec(raw.trim());
  if (!m) return undefined;
  const name = m[1].trim();
  if (name.length > CUSTOM_NAME_MAX) return undefined;
  const notes = m[2].trim().split(/\s+/);
  if (notes.length < 4 || notes.length > 12 || !notes.every(isValidNote)) return undefined;
  return { name: name || CUSTOM_DEFAULT_NAME, notes, strings: notes.length as StringCount };
}

export const customTuningId = (notes: string[]): string => `custom:${notes.join('-')}`;

/**
 * Which instrument a custom note list belongs to. Bass when it has 4-5 strings, or up to 8 strings with the lowest
 * note at C1 or below (a 6-8 string bass reaches B0/F#0), or more than 10 strings (multi-course); otherwise guitar.
 */
export function inferInstrument(notes: string[]): Instrument {
  if (notes.length <= 5 || notes.length > 10) return 'bass';
  if (notes.length <= 8 && Math.min(...notes.map(noteToMidi)) <= 24) return 'bass';
  return 'guitar';
}

function synthesizeCustom(name: string, notes: string[], full: string): Tuning {
  return { id: customTuningId(notes), instrument: inferInstrument(notes), name: full || formatCustomTuning(name, notes), strings: notes.length as StringCount, notes, category: 'custom' };
}

/** Catalogue tuning with exactly these notes (low to high), if any. */
export function findByNotes(notes: string[]): Tuning | undefined {
  return TUNINGS.find((t) => t.notes.length === notes.length && t.notes.every((n, i) => n === notes[i]));
}

/** Resolve a `custom:<notes joined by dash>` id (tuner presets). Catalogue entry when the notes match one. */
export function customTuningFromId(id: string | undefined | null, name = CUSTOM_DEFAULT_NAME): Tuning | undefined {
  if (!id || !id.startsWith('custom:')) return undefined;
  const parsed = parseCustomTuning(`${CUSTOM_DEFAULT_NAME} (${id.slice(7).split('-').join(' ')})`);
  if (!parsed) return undefined;
  return findByNotes(parsed.notes) ?? synthesizeCustom(name, parsed.notes, formatCustomTuning(name, parsed.notes));
}

// ---- lookup (canonical names, flats/sharps, legacy values) -----------------------------------------------------------

const FLAT_TO_SHARP: Record<string, string> = { ab: 'g#', bb: 'a#', cb: 'b', db: 'c#', eb: 'd#', fb: 'e', gb: 'f#' };

/** Lower-case, ♯/♭ -> #/b, flats -> sharps ("Drop Bb" == "Drop A#"), single spaces. */
export function tuningKey(raw: string): string {
  return raw
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b')
    .toLowerCase()
    .replace(/\b([a-g])b\b/g, (m) => FLAT_TO_SHARP[m] ?? m)
    .replace(/\s+/g, ' ')
    .trim();
}

// Legacy / shorthand values that exist in the DB, in local libraries and in old tests.
const LEGACY_ALIASES: Record<string, string> = {
  'e std': 'standard-e',
  standard: 'standard-e',
  'standard e': 'standard-e',
  'e-standard': 'standard-e',
  'b standard': 'b-standard',
  'b std': 'b-standard',
  'b std 7': 'b-standard',
  '7-string': 'b-standard',
  '7 string': 'b-standard',
  'drop a# / bb': 'drop-a-sharp'
};

const BY_KEY = new Map<string, Tuning>();
for (const t of TUNINGS) BY_KEY.set(tuningKey(t.name), t);

/** The catalogue entry (or a synthesized custom tuning) for a stored/typed tuning string, or undefined for unknown values. */
export function findTuning(raw: string | undefined | null): Tuning | undefined {
  if (!raw) return undefined;
  const key = tuningKey(raw);
  const direct = BY_KEY.get(key);
  if (direct) return direct;
  const legacy = LEGACY_ALIASES[key];
  if (legacy) return TUNINGS.find((t) => t.id === legacy);
  const custom = parseCustomTuning(raw);
  if (!custom) return undefined;
  // Exactly the notes of a catalogue tuning: prefer the catalogue entry.
  return findByNotes(custom.notes) ?? synthesizeCustom(custom.name, custom.notes, raw.trim());
}

/** Every string that may be stored for this tuning (canonical + legacy spellings), for API filters. */
export function tuningQueryValues(t: Tuning): string[] {
  const aliases = Object.entries(LEGACY_ALIASES)
    .filter(([, id]) => id === t.id)
    .map(([alias]) => alias);
  return Array.from(new Set([t.name, ...aliases]));
}

/** Short label for cards and chips; keeps today's "E Std" / "B Std 7". Unknown values are shown as they are. */
export function tuningBadge(raw: string | undefined | null): string {
  const t = findTuning(raw);
  if (!t) return raw?.trim() || 'E Std';
  if (t.category === 'custom') return parseCustomTuning(raw)?.name ?? t.name;
  if (t.id === 'standard-e') return 'E Std';
  if (t.id === 'b-standard') return 'B Std 7';
  return t.name.replace(/ Standard\b/, ' Std').replace(/^(\d+)-String /, '$1-Str ');
}

/** Full label for headers and details (canonical name). */
export function tuningLabel(raw: string | undefined | null): string {
  const t = findTuning(raw);
  if (t?.category === 'custom') return t.name;
  return t?.name ?? (raw?.trim() || 'E Standard');
}

/**
 * Match a tuning read from a Guitar Pro track (MIDI numbers as alphaTab reports them: highest string first)
 * to the catalogue. Undefined when it is not a known tuning.
 */
export function detectTuning(midiHighToLow: number[]): Tuning | undefined {
  const low = [...midiHighToLow].reverse();
  return TUNINGS.find((t) => t.notes.length === low.length && t.notes.every((n, i) => noteToMidi(n) === low[i]));
}

/** Tuner preset (string 1 = highest) for a catalogue tuning. Frequencies at A4 = 440; the tuner rescales for other A4. */
export function tuningToPreset(t: Tuning): TuningPreset {
  const count = t.notes.length;
  return {
    id: t.id,
    name: t.name,
    nameFa: t.name,
    nameEn: t.name,
    notes: t.notes.map((n, i) => {
      const { note, octave } = midiToNote(noteToMidi(n));
      return { stringNumber: count - i, note, octave, frequency: Number(noteFrequency(n).toFixed(2)) };
    })
  };
}
